// API utility for calling the Render.com auth service

import config from './config';

// Set this to your actual Render.com service URL and token
export const AUTH_SERVICE_URL = 'https://document-scanner-app.onrender.com';
// Token management - supports both JWT (dev) and Cognito tokens (production)
let cognitoUser = null; // Store Cognito user object globally

export function setCognitoUser(user) {
  cognitoUser = user;
}

export function getCognitoUser() {
  return cognitoUser;
}

export function setAuthToken(token) {
  localStorage.setItem('jwt_token', token);
}

export function getAuthToken() {
  // In production with Cognito, return Cognito ID token (contains email/user info)
  if (cognitoUser) {
    // Prefer id_token (contains email), fallback to access_token
    return cognitoUser.id_token || cognitoUser.access_token || null;
  }
  // Fallback to JWT token from localStorage (for dev mode)
  return localStorage.getItem('jwt_token');
}

/**
 * Log Cognito authentication event to backend for HIPAA compliance
 * @param {Object} user - Cognito user object from react-oidc-context
 * @returns {Promise<Object>} Response from backend
 */
export async function logCognitoLogin(user) {
  try {
    // Extract user info from Cognito user object
    const cognitoSub = user?.profile?.sub || null;
    const email = user?.profile?.email || user?.profile?.email_verified || null;
    const username = email ? email.split('@')[0] : (user?.profile?.username || cognitoSub || null);
    
    // Try to parse user_id from sub (Cognito sub is usually a UUID)
    // For now, send the sub as-is and let backend handle it
    let userId = null;
    if (cognitoSub) {
      // Try to extract numeric part or use hash
      try {
        // If sub contains numbers, try to extract
        const numericMatch = cognitoSub.match(/\d+/);
        if (numericMatch) {
          userId = parseInt(numericMatch[0]);
        }
      } catch (e) {
        // Keep as null, backend will generate from sub
      }
    }
    
    const response = await fetch(`${config.apiBaseUrl}/auth/cognito-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        email: email,
        username: username,  // Send username from frontend
        cognito_sub: cognitoSub,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to log login event' }));
      console.error('Failed to log Cognito login:', errorData);
      // Don't throw - logging failure shouldn't break the app
      return { success: false, error: errorData.error };
    }

    return await response.json();
  } catch (error) {
    console.error('Error logging Cognito login:', error);
    // Don't throw - logging failure shouldn't break the app
    return { success: false, error: error.message };
  }
}

/**
 * Log logout event to backend for HIPAA compliance
 * @returns {Promise<Object>} Response from backend
 */
export async function logLogout() {
  try {
    const token = getAuthToken();
    const response = await fetch(`${config.apiBaseUrl}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to log logout event' }));
      console.error('Failed to log logout:', errorData);
      // Don't throw - logging failure shouldn't break the app
      return { success: false, error: errorData.error };
    }

    return await response.json();
  } catch (error) {
    console.error('Error logging logout:', error);
    // Don't throw - logging failure shouldn't break the app
    return { success: false, error: error.message };
  }
}

export async function searchPatients({ name = '', dob = '', admit_date = '', hospital = '', exclude_scanned_by_user_id = null } = {}) {
  const token = getAuthToken();
  const params = new URLSearchParams();
  if (name) params.append('name', name);
  if (dob) params.append('dob', dob);
  if (admit_date) params.append('admit_date', admit_date);
  if (hospital) params.append('hospital', hospital);
  if (exclude_scanned_by_user_id) params.append('exclude_scanned_by_user_id', exclude_scanned_by_user_id);
  const url = `${config.apiBaseUrl}/patients/search?${params.toString()}`;
  const response = await fetch(url, {
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to search patients');
  }
  return response.json();
}

export async function fetchPatients(page = 1, per_page = 10, { excludeUserId = null } = {}) {
  const token = getAuthToken();
  let url = `${config.apiBaseUrl}/patients?page=${page}&per_page=${per_page}`;
  if (excludeUserId) {
    url += `&exclude_scanned_by_user_id=${excludeUserId}`;
  }
  const response = await fetch(url, {
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch patients');
  }
  return response.json();
}

export async function fetchDocuments(patientId) {
  const token = getAuthToken();
  const response = await fetch(`${config.apiBaseUrl}/patients/${patientId}/documents`, {
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch documents');
  }
  return response.json();
}

export async function fetchDocumentBlob(b2_file_id) {
  const token = getAuthToken();
    const response = await fetch(`${config.apiBaseUrl}/documents/${b2_file_id}`, {
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to fetch document' }));
    throw new Error(errorData.error || 'Failed to fetch document');
  }
  return response.blob();
}

export const fetchAdminScanStats = async () => {
  const token = getAuthToken();
  try {
    const response = await fetch(`${config.apiBaseUrl}/admin/scan_stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { message: 'An unknown error occurred on the server.' };
      }
      // Log error to file instead of console for HIPAA compliance
      logToFile('Failed to fetch admin scan stats. Server responded with:', response.status, errorData);
      throw new Error(errorData.error || 'Failed to fetch admin scan stats');
    }

    return response.json();
  } catch (error) {
    // Log error to file instead of console for HIPAA compliance
    logToFile('Network or other error fetching admin scan stats:', error);
    throw error; // Re-throw the error to be caught by the calling component
  }
};

/**
 * Fetch productivity data for employees scanning documents
 * @param {string} dateRange - Time range for metrics (today, week, month)
 * @returns {Promise<Array>} - Array of employee productivity metrics
 */
export const fetchProductivityData = async (dateRange = 'today') => {
  const token = getAuthToken();
  
  try {
    // First get the raw scan stats from the admin endpoint
    const response = await fetch(`${config.apiBaseUrl}/admin/scan_stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { message: 'An unknown error occurred on the server.' };
      }
      throw new Error(errorData.error || 'Failed to fetch admin scan stats');
    }
    
    const scanStats = await response.json();
    
    // Process the scan stats based on date range
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().split('T')[0];
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()).toISOString().split('T')[0];
    
    // Filter stats based on dateRange
    let filteredStats = scanStats;
    if (dateRange === 'today') {
      filteredStats = scanStats.filter(stat => stat.date === today);
    } else if (dateRange === 'week') {
      filteredStats = scanStats.filter(stat => stat.date >= oneWeekAgo);
    } else if (dateRange === 'month') {
      filteredStats = scanStats.filter(stat => stat.date >= oneMonthAgo);
    }
    
    // Group by employee and calculate metrics
    const employeeMetricsMap = new Map();
    
    filteredStats.forEach(stat => {
      const employeeId = stat.employee_id;
      const employeeName = stat.employee_name;
      
      if (!employeeMetricsMap.has(employeeId)) {
        employeeMetricsMap.set(employeeId, {
          id: employeeId,
          name: employeeName,
          totalPages: 0,
          uploadSessions: [],
          firstScanTime: null,
          lastScanTime: null,
          daysWorked: new Set()
        });
      }
      
      const employee = employeeMetricsMap.get(employeeId);
      employee.totalPages += stat.scanned_pages;
      employee.daysWorked.add(stat.date);
      
      // Track session timing for hours calculation
      if (!employee.firstScanTime || new Date(stat.clock_in) < new Date(employee.firstScanTime)) {
        employee.firstScanTime = stat.clock_in;
      }
      
      if (!employee.lastScanTime || new Date(stat.clock_out) > new Date(employee.lastScanTime)) {
        employee.lastScanTime = stat.clock_out;
      }
      
      // Add this as an upload session
      employee.uploadSessions.push({
        id: `${employeeId}-${stat.date}`,
        timestamp: stat.clock_in,
        pageCount: stat.scanned_pages
      });
    });
    
    // Convert map to array and calculate additional metrics
    const employeeMetrics = Array.from(employeeMetricsMap.values()).map(employee => {
      // Calculate hours worked
      let hoursWorked = 0;
      if (employee.firstScanTime && employee.lastScanTime) {
        // Calculate hours between first and last scan
        const firstScan = new Date(employee.firstScanTime);
        const lastScan = new Date(employee.lastScanTime);
        hoursWorked = (lastScan - firstScan) / (1000 * 60 * 60);
        
        // Cap at reasonable hours per day
        if (dateRange === 'today' && hoursWorked > 12) {
          hoursWorked = 12;
        }
      }
      
      return {
        id: employee.id,
        name: employee.name,
        hoursWorked: hoursWorked,
        totalPages: employee.totalPages,
        uploadSessions: employee.uploadSessions,
        daysWorked: employee.daysWorked.size
      };
    });
    
    // If no data is available, use mock data for demonstration
    if (employeeMetrics.length === 0) {
      return generateMockProductivityData(dateRange);
    }
    
    return employeeMetrics;
  } catch (error) {
    // Log error to file instead of console for HIPAA compliance
    logToFile('Error fetching productivity data:', error);
    
    // Fall back to mock data if there's an error
    return generateMockProductivityData(dateRange);
  }
};

/**
 * Log errors to file instead of console for HIPAA compliance
 * In a real implementation, this would write to a secure log file
 */
const logToFile = (...args) => {
  const timestamp = new Date().toISOString();
  const message = `${timestamp} - ${args.join(' ')}`;
  
  // In a real implementation, this would write to a secure log file
  // Here we're using a placeholder since we can't directly write to files from browser
  fetch(`${config.apiBaseUrl}/log-error`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify({ message }),
  }).catch(() => {
    // If logging fails, there's not much we can do in this fallback
  });
};

/**
 * Generate mock productivity data for demonstration purposes
 * This would be replaced by actual API data in production
 */
const generateMockProductivityData = (dateRange) => {
  // Actual employee usernames from the system
  const employees = [
    { id: 1, name: 'carepoint_jc' },
    { id: 2, name: 'carepoint_hoboken' },
    { id: 3, name: 'carepoint_hoboken2' },
    { id: 4, name: 'sanduke' }
  ];
  
  // Generate random productivity data for each employee
  return employees.map(employee => {
    // Hours worked varies by employee and date range
    let hoursWorked;
    if (dateRange === 'today') {
      hoursWorked = 2 + Math.random() * 6; // Between 2-8 hours
    } else if (dateRange === 'week') {
      hoursWorked = 20 + Math.random() * 20; // Between 20-40 hours
    } else { // month
      hoursWorked = 80 + Math.random() * 80; // Between 80-160 hours
    }
    
    // For different performance tiers:
    let pagesPerHourBase;
    const performanceGroup = employee.id % 4; // Distribute evenly across performance tiers
    
    switch (performanceGroup) {
      case 0: // Poor performer
        pagesPerHourBase = 200 + Math.random() * 80; // 200-280 pages/hour
        break;
      case 1: // Average performer
        pagesPerHourBase = 290 + Math.random() * 40; // 290-330 pages/hour
        break;
      case 2: // Good performer
        pagesPerHourBase = 340 + Math.random() * 60; // 340-400 pages/hour
        break;
      case 3: // Excellent performer
        pagesPerHourBase = 420 + Math.random() * 80; // 420-500 pages/hour
        break;
      default:
        pagesPerHourBase = 290 + Math.random() * 40; // Default to average
        break;
    }
    
    // Calculate total pages
    const totalPages = Math.floor(pagesPerHourBase * hoursWorked);
    
    // Generate mock upload sessions with varying batch sizes
    const numberOfSessions = Math.max(1, Math.floor(totalPages / 50)); // Roughly 50 pages per session
    const uploadSessions = [];
    
    // Add some variability to batch sizes based on performance group
    let batchVariability;
    switch (performanceGroup) {
      case 0: // Poor performer: high variability
        batchVariability = 0.6;
        break;
      case 1: // Average performer: moderate variability
        batchVariability = 0.4;
        break;
      case 2: // Good performer: low variability
        batchVariability = 0.25;
        break;
      case 3: // Excellent performer: very consistent
        batchVariability = 0.15;
        break;
      default:
        batchVariability = 0.4; // Default to average
        break;
    }
    
    // Base batch size varies by performer (better performers tend to batch more efficiently)
    const baseBatchSize = 25 + (performanceGroup * 10);
    
    // Generate the upload sessions
    let remainingPages = totalPages;
    for (let i = 0; i < numberOfSessions && remainingPages > 0; i++) {
      // Add variability to batch size
      const variabilityFactor = 1 + (Math.random() * 2 - 1) * batchVariability;
      let pageCount = Math.floor(baseBatchSize * variabilityFactor);
      
      // Make sure we don't exceed remaining pages
      pageCount = Math.min(pageCount, remainingPages);
      remainingPages -= pageCount;
      
      // Calculate session timestamp (spread throughout the day)
      const now = new Date();
      const hoursAgo = (numberOfSessions - i) * (hoursWorked / numberOfSessions);
      const timestamp = new Date(now.getTime() - hoursAgo * 3600 * 1000).toISOString();
      
      uploadSessions.push({
        id: `${employee.id}-${i}`,
        timestamp,
        pageCount,
      });
    }
    
    return {
      id: employee.id,
      name: employee.name,
      hoursWorked,
      totalPages,
      uploadSessions
    };
  });
};
